// ke-product-details — thumbnail switching, variant pills, fetch add-to-cart, complete-the-set adds
class KeProductDetails extends HTMLElement {
  connectedCallback() {
    const variantsEl = this.querySelector('[data-ke-variants]');
    try { this.variants = JSON.parse(variantsEl ? variantsEl.textContent : '[]'); } catch (e) { this.variants = []; }

    // Thumbnails
    this.thumbs = Array.from(this.querySelectorAll('.ke-pd__thumb'));
    this.images = Array.from(this.querySelectorAll('.ke-pd__main-img'));
    this.thumbs.forEach((t) => {
      t.addEventListener('click', () => this.showMedia(parseInt(t.dataset.mediaIndex, 10)));
    });

    // Variant pills
    this.optionFieldsets = Array.from(this.querySelectorAll('.ke-pd__option'));
    this.optionFieldsets.forEach((fs) => {
      fs.addEventListener('change', () => this.resolveVariant());
    });
    this.selectedLabel = this.querySelector('[data-ke-selected]');
    this.atc = this.querySelector('[data-ke-atc]');
    this.atcLabel = this.querySelector('[data-ke-atc-label]');
    this.atcPrice = this.querySelector('[data-ke-atc-price]');
    this.compareEl = this.querySelector('[data-ke-compare]');
    this.priceEl = this.querySelector('[data-ke-price]');
    this.priceCompareEl = this.querySelector('[data-ke-price-compare]');
    // The real <form action="/cart/add"> and its variant input. PDP apps (Kaching and
    // friends) read the selected variant from input[name="id"], so it must stay in sync.
    this.form = this.querySelector('.ke-pd__form');
    this.variantInput = this.querySelector('[data-ke-variant-input]');
    // Capture the server-rendered (translated) label here, not inside resolveVariant —
    // single-variant products never run the option-change path that would otherwise set it.
    if (this.atcLabel) this.atcLabel.dataset.original = this.atcLabel.textContent;
    this.resolveVariant(true);

    // Variant cards select a variant by id directly, so they bypass the option-matching
    // path used by the text pills. Everything downstream (cart form input, price, ATC
    // state) still has to stay in sync or the customer buys the wrong colour.
    this.variantCards = Array.from(this.querySelectorAll('[data-ke-variant-option]'));
    this.variantCards.forEach((input) => {
      input.addEventListener('change', () => {
        const variant = this.variants.find((v) => String(v.id) === String(input.value));
        if (!variant) return;
        this.querySelectorAll('.ke-vc__card').forEach((c) => c.classList.remove('is-selected'));
        const card = input.closest('.ke-vc__card');
        if (card) card.classList.add('is-selected');
        this.applyVariant(variant);
      });
    });

    if (this.form) {
      // Intercept the native submit so we keep the AJAX add + cart drawer. If this script
      // never runs, the browser posts the form normally and the customer can still buy.
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.add(this.variantInput ? this.variantInput.value : this.atc.dataset.variantId, this.atc);
      });
    } else if (this.atc) {
      // Legacy markup without a form.
      this.atc.addEventListener('click', () => this.add(this.atc.dataset.variantId, this.atc));
    }

    this.querySelectorAll('[data-ke-set-add]').forEach((b) => {
      b.addEventListener('click', () => this.add(b.dataset.variantId, b));
    });

    // Full ingredients popup
    this.ingredientsModal = this.querySelector('[data-ke-ingredients-modal]');
    if (this.ingredientsModal) {
      this.querySelectorAll('[data-ke-ingredients-open]').forEach((b) => {
        b.addEventListener('click', () => { this.ingredientsModal.hidden = false; document.body.style.overflow = 'hidden'; });
      });
      this.ingredientsModal.querySelectorAll('[data-ke-ingredients-close]').forEach((b) => {
        b.addEventListener('click', () => { this.ingredientsModal.hidden = true; document.body.style.overflow = ''; });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.ingredientsModal.hidden) { this.ingredientsModal.hidden = true; document.body.style.overflow = ''; }
      });
    }
  }

  showMedia(index) {
    this.thumbs.forEach((t) => t.classList.toggle('is-selected', parseInt(t.dataset.mediaIndex, 10) === index));
    this.images.forEach((img) => img.classList.toggle('is-selected', parseInt(img.dataset.mediaIndex, 10) === index));
  }

  currentOptions() {
    return this.optionFieldsets.map((fs) => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  // Push a chosen variant into every part of the buy box. Used by the image variant
  // cards, which pick a variant by id rather than by matching option values.
  applyVariant(variant) {
    if (!variant) return;
    if (this.atc) {
      this.atc.dataset.variantId = variant.id;
      this.atc.disabled = !variant.available;
    }
    // The cart form input is what add-to-cart and PDP apps actually read.
    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.atcLabel) {
      this.atcLabel.textContent = variant.available
        ? this.atcLabel.dataset.original
        : (this.atcLabel.dataset.soldOut || 'Slutsåld');
    }
    if (this.atcPrice) this.atcPrice.textContent = this.money(variant.price);
    if (this.priceEl) this.priceEl.textContent = this.money(variant.price);

    const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (this.priceCompareEl) {
      this.priceCompareEl.hidden = !hasCompare;
      if (hasCompare) this.priceCompareEl.textContent = this.money(variant.compare_at_price);
    }
    if (this.compareEl) {
      this.compareEl.hidden = !hasCompare;
      if (hasCompare) this.compareEl.textContent = this.money(variant.compare_at_price);
    }
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  resolveVariant(initial) {
    if (!this.optionFieldsets.length || !this.variants.length) return;
    const options = this.currentOptions();
    const variant = this.variants.find((v) => options.every((val, i) => v.options[i] === val));

    if (this.selectedLabel) this.selectedLabel.textContent = options.filter(Boolean).join(' / ');
    if (!this.atc) return;

    if (!variant) {
      this.atc.disabled = true;
      if (this.atcLabel) this.atcLabel.textContent = this.atcLabel.dataset.unavailable || 'Ej tillgänglig';
      return;
    }
    this.atc.dataset.variantId = variant.id;
    // Keep the cart form's variant input current — this is what PDP apps read.
    if (this.variantInput) this.variantInput.value = variant.id;
    this.atc.disabled = !variant.available;
    if (this.atcLabel && !initial) {
      this.atcLabel.textContent = variant.available
        ? this.atcLabel.dataset.original
        : (this.atcLabel.dataset.soldOut || 'Slutsåld');
    }
    if (this.atcPrice) this.atcPrice.textContent = this.money(variant.price);
    // Standalone price block (only present when that block is used).
    if (this.priceEl) this.priceEl.textContent = this.money(variant.price);
    if (this.priceCompareEl) {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.priceCompareEl.hidden = !hasCompare;
      if (hasCompare) this.priceCompareEl.textContent = this.money(variant.compare_at_price);
    }
    if (this.compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compareEl.hidden = false;
        this.compareEl.textContent = this.money(variant.compare_at_price);
      } else {
        this.compareEl.hidden = true;
      }
    }
    // Keep the URL shareable with the selected variant
    if (!initial && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  money(cents) {
    // Format for the shop's own locale, not a hardcoded pattern. The previous version
    // produced "1599.00 SEK" where Shopify's Liquid renders "1 599,00 kr", so switching
    // variant visibly changed the price format on the page.
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SEK';
    const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'sv-SE';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  add(variantId, button) {
    if (!variantId || button.disabled) return;
    // Just add to the cart — no color flash, no text swap. Being in the cart is the confirmation.
    button.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then((r) => {
        button.disabled = false;
        if (r.ok) document.dispatchEvent(new CustomEvent('ke:cart:added'));
      })
      .catch(() => { button.disabled = false; });
  }
}
customElements.define('ke-product-details', KeProductDetails);
